import React from 'react';
import { ApToggleSwitch } from '@alixpartners/ui-components';

import { QueryStringContext } from 'contexts/QueryStringContext';
import { SettingsContext } from 'contexts/SettingsContext';
import { VENDOR_TYPES, VENDOR_DIMENSIONS } from 'Details/utils';
import { serializeQueryParams } from 'utils/api';
import { createStoredQuery, fetchStoredQuery } from 'utils/apiRequests';
import { CustomFilters } from '../CustomFilters';

import {
	Dropdown,
	ToggleWrapper,
	ToggleRow,
	Divider,
	DividerFilter,
	RadioWrapper,
	Radio,
} from './MoreFilters.styles';

const CUSTOM_FILTERS_QUERY_STRING_LIMIT = 2048;

export class MoreFilters extends React.Component {
	state = {
		open: false,
		selectedFilter: null,
		loading: false,
	};

	componentDidMount() {
		this.fetchStoredFilters();
	}

	componentDidUpdate(prevProps) {
		const { storedQueryId, setQueryStringState } = this.props;

		if (!storedQueryId && !!prevProps.storedQueryId) {
			setQueryStringState({
				storedQuery: null,
			});
		} else if (storedQueryId !== prevProps.storedQueryId) {
			this.fetchStoredFilters();
		}
	}

	async fetchStoredFilters() {
		const { storedQueryId, setQueryStringState } = this.props;

		if (storedQueryId) {
			const query = await fetchStoredQuery(storedQueryId);

			const customFilters = query
				.filter(item => item.key === 'filters')
				.map(item => item.value);
			const customFiltersValues = query
				.filter(item => item.key === 'filterValues')
				.map(item => item.value);

			setQueryStringState({
				storedQuery: {
					customFilters,
					customFiltersValues,
				},
			});
		}
	}

	getCustomFilters() {
		const { storedQueryId, storedQuery } = this.props;
		let { customFiltersValues, customFilters } = this.props;

		if (storedQueryId) {
			if (storedQuery) {
				({ customFiltersValues, customFilters } = storedQuery);
			} else {
				customFiltersValues = [];
				customFilters = [];
			}
		}

		return { customFiltersValues, customFilters };
	}

	closeFilter = () => {
		this.setState({ selectedFilter: null, loading: false });
	};

	selectFilter = filter => {
		this.setState({ selectedFilter: filter });
	};

	handleChangeOpen = open => {
		this.setState({ open });
	};

	handleSourceableChange = isSourceable => {
		const { setQueryStringStateAndPush } = this.props;

		setQueryStringStateAndPush({
			showNonSourceable: !isSourceable,
			measures: undefined,
		});
	};

	handleHarmonizedChange = showHarmonizedVendors => {
		const { setQueryStringStateAndPush, dimensions } = this.props;
		const queryParams = {
			showHarmonizedVendors,
		};

		if (dimensions && VENDOR_DIMENSIONS.includes(dimensions[0])) {
			const currentDimension = showHarmonizedVendors
				? VENDOR_TYPES.harmonized
				: VENDOR_TYPES.original;
			queryParams.dimensions = new Array(2).fill(currentDimension);
		}

		setQueryStringStateAndPush(queryParams);
	};

	clearAll = () => {
		const { setQueryStringStateAndPush, setQueryStringState } = this.props;

		setQueryStringState({ storedQuery: undefined });

		setQueryStringStateAndPush({
			storedQueryId: undefined,
			customFiltersValues: [],
			customFilters: [],
		});
	};

	onFilterChange = async queryParams => {
		const { setQueryStringStateAndPush, setQueryStringState } = this.props;
		const locationHref =
			(window && window.location && window.location.href) || '';
		// TODO: use logic to serialize all qury url
		const isQueryParamsExceeded =
			`${locationHref}&${serializeQueryParams({
				filterValues: [...queryParams.customFiltersValues],
				filters: [...queryParams.customFilters],
			})}`.length > CUSTOM_FILTERS_QUERY_STRING_LIMIT;

		if (isQueryParamsExceeded) {
			// show loading
			this.setState({
				loading: true,
			});

			// store query
			const requestParams = [
				...queryParams.customFilters.map(value => ({
					key: 'filters',
					value,
				})),
				...queryParams.customFiltersValues.map(value => ({
					key: 'filterValues',
					value,
				})),
			];

			const { id: storedQueryId } = await createStoredQuery(requestParams);

			// save filters to store here - to prevent un-needed GET request
			setQueryStringState({ storedQuery: queryParams });

			// clear filters here if storedQueryId
			setQueryStringStateAndPush({
				storedQueryId,
				customFiltersValues: [],
				customFilters: [],
			});
		} else {
			// clear stored filters
			setQueryStringState({ storedQuery: undefined });

			// save filters to query params
			setQueryStringStateAndPush({ ...queryParams, storedQueryId: undefined });
		}

		this.closeFilter();
	};

	render() {
		const { open, selectedFilter, loading } = this.state;
		const {
			options,
			showNonSourceable,
			showHarmonizedVendors,
			hideAddressableSelect,
			hideHarmonizedVendorsSelect,
		} = this.props;
		let { customFiltersValues, customFilters } = this.getCustomFilters();
		const isSourceable = showNonSourceable !== 'true';
		const isHarmonized = showHarmonizedVendors === 'true';

		return (
			<Dropdown
				label="More Filters"
				hideOpenLabel
				open={open}
				onChangeOpen={this.handleChangeOpen}
			>
				{!selectedFilter && (
					<React.Fragment>
						<ToggleWrapper>
							{!hideAddressableSelect && (
								<RadioWrapper>
									<Radio
										name="showSourceableSpend"
										defaultChecked={!!isSourceable}
										onChange={_0 => this.handleSourceableChange(true)}
									>
										Show sourceable spend
									</Radio>
									<Radio
										name="showSourceableSpend"
										defaultChecked={!isSourceable}
										onChange={_0 => this.handleSourceableChange(false)}
									>
										Show non-sourceable spend
									</Radio>
								</RadioWrapper>
							)}
							{!hideHarmonizedVendorsSelect && (
								<React.Fragment>
									<DividerFilter />
									<ToggleRow>
										Show harmonized vendor names
										<ApToggleSwitch
											defaultChecked={isHarmonized}
											onChange={e =>
												this.handleHarmonizedChange(e.target.checked)
											}
										/>
									</ToggleRow>
								</React.Fragment>
							)}
						</ToggleWrapper>
					</React.Fragment>
				)}

				{options &&
					options.filter(({ isActive }) => isActive).length > 0 && (
						<React.Fragment>
							{!selectedFilter && <Divider />}
							<CustomFilters
								options={options}
								customFilters={customFilters}
								customFiltersValues={customFiltersValues}
								onFilterChange={this.onFilterChange}
								clearAll={this.clearAll}
								selectedFilter={selectedFilter}
								closeFilter={this.closeFilter}
								selectFilter={this.selectFilter}
								loading={loading}
							/>
						</React.Fragment>
					)}
			</Dropdown>
		);
	}
}

export default props => (
	<SettingsContext.Consumer>
		{({ customFilters: options }) => (
			<QueryStringContext.Consumer>
				{({
					setQueryStringStateAndPush,
					setQueryStringState,
					customFilters,
					customFiltersValues,
					showNonSourceable,
					showHarmonizedVendors,
					dimensions,
					storedQueryId,
					storedQuery,
				}) => (
					<MoreFilters
						{...props}
						options={options}
						customFilters={customFilters}
						dimensions={dimensions}
						showNonSourceable={showNonSourceable}
						customFiltersValues={customFiltersValues}
						showHarmonizedVendors={showHarmonizedVendors}
						setQueryStringStateAndPush={setQueryStringStateAndPush}
						setQueryStringState={setQueryStringState}
						storedQueryId={storedQueryId}
						storedQuery={storedQuery}
					/>
				)}
			</QueryStringContext.Consumer>
		)}
	</SettingsContext.Consumer>
);
